import { TrendingDown, XCircle, CheckCircle2, BookCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

function formatEur(n) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
  }).format(Number(n ?? 0));
}

/** Amounts arrive as numbers (r2) or preformatted "123.45€" strings. */
function fmtAmount(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'number') return formatEur(v);
  return v;
}

const METHOD_LABELS = {
  linear: 'Lineal', lineal: 'Lineal',
  declining: 'Decreciente', degresivo: 'Decreciente',
};
function methodLabel(m) {
  if (!m) return '';
  return METHOD_LABELS[String(m).toLowerCase()] ?? m;
}

/**
 * DepreciationBatchPreview — rich two-step approval card for POST_DEPRECIATION_BATCH.
 *
 * The generic PendingActionCard drops the nested `activos` array, so the user
 * could not see which fixed assets would be depreciated before confirming.
 * This card surfaces the per-asset table (name, method, monthly amount, whether
 * it was already posted) plus the batch totals.
 *
 * Backend preview shape (raw keys via toolExecutor `_raw`):
 *   { mes, year, total_activos, total_importe, ya_contabilizados,
 *     activos: [{ nombre, metodo, importe_mensual, ya_contabilizado }] }
 *
 * Props: action { type, preview }, onApprove, onReject, status
 */
export default function DepreciationBatchPreview({ action, onApprove, onReject, status }) {
  const { preview } = action;

  if (status === 'approved') {
    return (
      <div className="flex items-center gap-2 p-2 bg-success/10 border border-success/30 rounded-lg text-xs text-success my-1">
        <BookCheck className="w-3.5 h-3.5" />
        <span>Amortización aprobada — contabilizando…</span>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="flex items-center gap-2 p-2 bg-muted border border-border rounded-lg text-xs text-muted-foreground my-1">
        <XCircle className="w-3.5 h-3.5" />
        <span>Contabilización de amortización cancelada</span>
      </div>
    );
  }

  const activos = preview?.activos ?? [];
  const mes = preview?.mes;
  const year = preview?.year;
  const periodo = mes != null && year != null
    ? `${String(mes).padStart(2, '0')}/${year}`
    : null;
  const yaContabilizados = Number(preview?.ya_contabilizados ?? 0);
  const totalActivos = Number(preview?.total_activos ?? activos.length);
  const aPostear = totalActivos - yaContabilizados;

  return (
    <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-950/20 p-4 space-y-3 my-2 max-w-sm">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Badge variant="outline" className="text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700 text-[10px] mb-1">
            Requiere aprobación
          </Badge>
          <p className="text-sm font-semibold text-foreground leading-tight flex items-center gap-1">
            <TrendingDown className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400 shrink-0" />
            Amortización{periodo ? ` ${periodo}` : ''}
          </p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {aPostear} activo{aPostear !== 1 ? 's' : ''} a contabilizar
            {yaContabilizados > 0 ? ` · ${yaContabilizados} ya contabilizado${yaContabilizados !== 1 ? 's' : ''}` : ''}
          </p>
        </div>
        <span className="text-base font-bold text-indigo-700 dark:text-indigo-300 shrink-0 tabular-nums">
          {fmtAmount(preview?.total_importe)}
        </span>
      </div>

      {/* Assets table */}
      {activos.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-indigo-200 dark:border-indigo-800 text-muted-foreground">
                <th className="text-left pb-1 font-medium">Activo</th>
                <th className="text-left pb-1 font-medium">Método</th>
                <th className="text-right pb-1 font-medium">Cuota</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-indigo-100 dark:divide-indigo-900/40">
              {activos.map((a, i) => (
                <tr key={i} className={a.ya_contabilizado ? 'opacity-50' : ''}>
                  <td className="py-1 pr-2 text-foreground max-w-[140px] truncate">
                    {a.nombre || '—'}
                    {a.ya_contabilizado && (
                      <span className="block text-[9px] text-muted-foreground">ya contabilizado</span>
                    )}
                  </td>
                  <td className="py-1 pr-2 text-muted-foreground">{methodLabel(a.metodo)}</td>
                  <td className="py-1 text-right tabular-nums text-foreground">
                    {fmtAmount(a.importe_mensual)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-indigo-200 dark:border-indigo-800 font-semibold text-foreground">
                <td className="pt-1.5 text-left" colSpan={2}>Total</td>
                <td className="pt-1.5 text-right tabular-nums">{fmtAmount(preview?.total_importe)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground flex items-center gap-1">
          <CheckCircle2 className="w-3.5 h-3.5 text-success" />
          No hay activos pendientes de amortizar en este período.
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onReject} className="flex-1 h-7 text-xs">
          <XCircle className="w-3 h-3 mr-1" />Cancelar
        </Button>
        <Button size="sm" onClick={() => onApprove()} disabled={aPostear <= 0}
          className="flex-1 h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50">
          <BookCheck className="w-3 h-3 mr-1" />Contabilizar
        </Button>
      </div>
    </div>
  );
}
